import React from "react"
import { ConfigurablePropsFromDescription, useWidgetId } from "../../../helpers/widget"
import PublishUtils from "../../../helpers/PublishUtils"
import { EMPTY_IFC_TO_LD_CONFIG, IfcToLdConfig } from "./ifc-to-ld-config"
import { Override } from "../../../utils/typescript"
import { JsonTextEditor } from "../../JsonEditor"
import { iframeConnectionsByWidgetId } from "./ifc-viewer"
import { Button } from "../../ui-primitives/button"

export const configOptions = [
  {
    name: 'ifcUrls',
    type: 'text',
    label: 'IFC URLs to load and view (not the UploadAnyFile URI)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'fragUrls',
    type: 'text',
    label: 'Fragment URLs to load and view (not the UploadAnyFile URI)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'autoConvertToFrag',
    type: 'boolean',
    label: 'Automatically convert any IFCs that are loaded to Frag files. (Converting requires full-access permissions)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'autoConvertToLd',
    type: 'boolean',
    label: 'Automatically convert any Fragments that are loaded to LD files. IFCs will need to be converted to Fragments first. (Converting requires full-access permissions)',
    topicValueInterpolationEnabled: true
  },
  {
    name: 'ifcLdGraph',
    type: 'text',
    label: 'Target graph for IFC Linked Data. Use {{generated_uuid}} for random UUID. You can also use other app variables.'
  },
  {
    name: 'ifcToLdConfig',
    type: 'json-text',
    label: 'Configuration for JSON-to-LD',
    helpComponent: IfcToLdConfigExampleGenerator
  },
  PublishUtils.getMultipleValueFormatOptions(),
  PublishUtils.getTimestampParametersDef()
] as const

export type RawIFCViewerV4ConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>

export type IFCViewerV4ConfigOptions = Override<RawIFCViewerV4ConfigOptions, {
  ifcToLdConfig?: IfcToLdConfig | null,
  _parsingErrors: Error[]
}>

function IfcToLdConfigExampleGenerator() {
  const widgetId = useWidgetId()
  const [ example, setExample ] = React.useState<IfcToLdConfig>(EMPTY_IFC_TO_LD_CONFIG)

  async function generateExample() {
    const iframe = iframeConnectionsByWidgetId[widgetId]
    if (!iframe || !iframe.current) alert("Current IFC widget is not loaded or has broken IFrame connection")
    try {
      const example = await iframe.current?.generateExampleIfcToLdConfig()
      if (!example) throw new Error("IFrame returned null example")
      setExample(example)
    } catch (e) {
      console.error(e)
      alert("Error generating example" + e)
    }
  }

  return (
    <div>
      <h1 className="text-lg font-bold mb-1">Example configuration</h1>
      <JsonTextEditor value={JSON.stringify(example, null, 4)} />

      <div className="my-2">
        <Button variant="default" onClick={generateExample}>
          Generate example based on data of currently loaded IFCs
        </Button>
      </div>

      <p>All properties are optional.</p>
      <p>Use <code>null</code> to export all attributes/categories/relations.
        But depending on the IFC, this may be slow and generate a lot of data.</p>
    </div>
  )
}
